import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useStore from '../hooks/useStore'

function Auth() {
  const navigate = useNavigate()
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [authMode, setAuthMode] = useState('auth')

  const { user, signIn, signUp, signOut, resetPasswordForEmail, updatePassword } = useStore()

  useEffect(() => {
    const isRecovery = window.location.hash.includes('type=recovery')
    if (isRecovery) {
      setAuthMode('reset')
      setError('')
    }
  }, [])

  useEffect(() => {
    if (user && authMode !== 'reset') {
      navigate('/', { replace: true })
    }
  }, [user, authMode, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (authMode === 'forgot') {
      if (!email) {
        setError('Enter your email first.')
        return
      }

      setLoading(true)
      setError('')
      try {
        await resetPasswordForEmail(email)
        setError('✓ Password reset link sent. Check your email.')
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
      return
    }

    if (authMode === 'reset') {
      if (!password || !confirmPassword) {
        setError('Fill in both password fields.')
        return
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters.')
        return
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.')
        return
      }

      setLoading(true)
      setError('')
      try {
        await updatePassword(password)
        await signOut()
        window.history.replaceState({}, document.title, '/auth')
        setPassword('')
        setConfirmPassword('')
        setAuthMode('auth')
        setIsLogin(true)
        setError('✓ Password updated. Sign in with your new password.')
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
      return
    }

    if (!email || !password) {
      setError('Fill in all fields.')
      return
    }
    if (!isLogin && password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setLoading(true)
    setError('')

    try {
      if (isLogin) {
        await signIn(email, password)
      } else {
        await signUp(email, password)
        setError('✓ Welcome to ResultOS. Check email to confirm — then let\'s make you unstoppable.')
        setIsLogin(true)
        return
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const renderHeader = () => {
    if (authMode === 'forgot') {
      return {
        title: 'Forgot Password',
        subtitle: 'Enter your email and we will send you a reset link.'
      }
    }

    if (authMode === 'reset') {
      return {
        title: 'Set New Password',
        subtitle: 'Choose a new password for your account.'
      }
    }

    if (!isLogin) {
      return {
        title: 'Become unstoppable',
        subtitle: 'Join ResultOS. Ship your W every day.'
      }
    }

    return {
      title: null,
      subtitle: null
    }
  }

  const headerCopy = renderHeader()

  return (
    <>
      <div className="ambient">
        <div className="orb orb1"></div>
        <div className="orb orb2"></div>
        <div className="orb orb3"></div>
      </div>
      <div className="auth-screen">
        <div className="auth-box">
          <div className="a-logo">Result<span>OS</span></div>
          <div className="a-tag">R = (W × C) + T · The Unstoppable System</div>
          {authMode === 'auth' ? (
            <div className="a-tabs">
              <button
                className={`a-tab ${isLogin ? 'on' : ''}`}
                onClick={() => setIsLogin(true)}
                type="button"
              >
                Sign In
              </button>
              <button
                className={`a-tab ${!isLogin ? 'on' : ''}`}
                onClick={() => setIsLogin(false)}
                type="button"
              >
                Create Account
              </button>
            </div>
          ) : null}
          {headerCopy.title ? <div className="auth-mode-title">{headerCopy.title}</div> : null}
          {headerCopy.subtitle ? <div className="auth-mode-sub">{headerCopy.subtitle}</div> : null}
          <form onSubmit={handleSubmit}>
            <label className="a-lbl" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              className="a-in"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            {authMode !== 'forgot' ? (
              <>
                <label className="a-lbl" htmlFor="auth-password">{authMode === 'reset' ? 'New Password' : 'Password'}</label>
                <input
                  id="auth-password"
                  className="a-in"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={authMode === 'reset' ? 'Min. 6 characters' : (isLogin ? '••••••••' : 'Min. 6 characters')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                  required
                />
              </>
            ) : null}
            {authMode === 'reset' ? (
              <>
                <label className="a-lbl" htmlFor="auth-confirm-password">Confirm Password</label>
                <input
                  id="auth-confirm-password"
                  className="a-in"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  required
                />
              </>
            ) : null}
            {authMode === 'auth' && isLogin ? (
              <button
                type="button"
                className="a-link"
                onClick={() => {
                  setAuthMode('forgot')
                  setError('')
                  setPassword('')
                }}
              >
                Forgot password?
              </button>
            ) : null}
            <div className="a-err">{error}</div>
            <button className="a-btn" disabled={loading} type="submit">
              {loading
                ? authMode === 'forgot'
                  ? 'Sending…'
                  : authMode === 'reset'
                    ? 'Updating…'
                    : isLogin
                      ? 'Signing in…'
                      : 'Creating…'
                : authMode === 'forgot'
                  ? 'Send Reset Link →'
                  : authMode === 'reset'
                    ? 'Update Password →'
                    : isLogin
                      ? 'Sign In →'
                      : 'Create Account →'}
            </button>
          </form>
          {authMode !== 'auth' ? (
            <button
              type="button"
              className="a-link a-link-secondary"
              onClick={() => {
                window.history.replaceState({}, document.title, '/auth')
                setAuthMode('auth')
                setIsLogin(true)
                setPassword('')
                setConfirmPassword('')
                setError('')
              }}
            >
              Back to Sign In
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default Auth
